// ============================================================================
// MarketFeedLiveBanner — состояние ЖИВОЙ ленты (одна строка, без окон/таблиц):
//   • нет связи / переподключение — живое обновление приостановлено (честно);
//   • есть новые заявки — счётчик и «Показать» (сброс индикатора);
//   • фоновая сверка не удалась — неблокирующее сообщение с повтором.
// Индикатор показывается только при включённой настройке (showUnseen).
// ============================================================================

import { AlertTriangle, BellRing, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConnStatus } from '@/lib/connection';

interface MarketFeedLiveBannerProps {
  connection: ConnStatus;
  /** Сколько новых заявок пришло живьём и ещё не просмотрено. */
  unseen: number;
  /** Показывать ли индикатор новых заявок (настройка пользователя). */
  showUnseen: boolean;
  /** Ошибка фоновой сверки (лента при этом не пропадает). */
  syncError?: string;
  onShowNew: () => void;
  onRetry?: () => void;
}

export function MarketFeedLiveBanner({
  connection,
  unseen,
  showUnseen,
  syncError,
  onShowNew,
  onRetry,
}: MarketFeedLiveBannerProps) {
  const paused = connection !== 'online';
  if (!paused && !(showUnseen && unseen > 0) && !syncError) return null;

  return (
    <div className="space-y-2">
      {paused && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-800">
          {connection === 'offline' ? (
            <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          ) : (
            <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
          )}
          <span>
            {connection === 'offline'
              ? 'Нет связи с сервером — новые заявки появятся после восстановления связи. Показаны сохранённые данные.'
              : 'Восстанавливаем связь — лента догонит изменения автоматически.'}
          </span>
        </div>
      )}

      {!paused && showUnseen && unseen > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-[11px] font-medium text-primary">
          <BellRing className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            {unseen === 1 ? 'Новая заявка' : `Новых заявок: ${unseen}`}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 rounded-lg border-primary/40 bg-background/70 px-2.5 text-[11px] text-primary"
            onClick={onShowNew}
          >
            Показать
          </Button>
        </div>
      )}

      {!paused && syncError && (
        <div className="flex items-start gap-2 rounded-xl bg-muted/60 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{syncError}</span>
          {onRetry && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 rounded-lg px-2 text-[11px]"
              onClick={onRetry}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Повторить
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
